// Core
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Libraries
import { ConfigContext } from 'antd/es/config-provider';
import type { RangeValue } from 'antd/node_modules/rc-picker/lib/interface';
import dayjs, { Dayjs } from 'dayjs';
import type { Dayjs as AntDDayjs } from 'antd/node_modules/dayjs';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint/useBreakpoint';

// Components
import { Close } from '../../icons';

// Component
import {
  DatePickerPlacement,
  DatePickerProps,
  DatePickerType,
} from './DatePicker.types';
import {
  DatePickerComponent,
  PanelContainer,
  RangePicker,
} from './DatePicker.style';

function DatePicker({
  className,
  disabled,
  onChange,
  placement = DatePickerPlacement.bottomLeft,
  testId,
  type = DatePickerType.rangePicker,
  value,
}: DatePickerProps): JSX.Element {
  // Dependencies
  const { locale } = useContext(ConfigContext);
  const {
    isXs,
    isSm,
    isMd,
    isLandscape,
  } = useBreakpoint();
  const isMobileOrLandscape: boolean = useMemo(
    (): boolean => (isXs || isSm) || ((isXs || isSm || isMd) && isLandscape),
    [isXs, isSm, isMd, isLandscape],
  );

  const [inputWidthByValue, setInputWidthByValue] = useState<number>(100);
  const [interimRange, setInterimRange] = useState<RangeValue<AntDDayjs> | null>(null);

  const datePickerFormat: string = locale?.locale === 'pt-br' ? 'DD MMM, YY' : 'MMM DD, YY';

  const getInputValueWidthByFont = (text: string, font: string): number => {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    const context: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (context) {
      context.font = font;
      return context.measureText(text).width;
    }
    return 100;
  };

  const calculateInputWidth = useCallback((): void => {
    const inputElement: HTMLInputElement | null = document.querySelector(
      '.ant-picker-range .ant-picker-input:first-child input',
    ) as HTMLInputElement | null;

    if (inputElement) {
      // Get the input value if it exists
      const inputValue: string = inputElement.value || '';

      // If there's no value, use the placeholder
      let textToMeasure: string;

      if (inputValue.length > 0) {
        // Use the current value if it exists
        textToMeasure = inputValue;
      } else {
        // Try to get the placeholder from attribute or use the default date format
        const placeholderValue: string = inputElement.getAttribute('placeholder') || datePickerFormat;
        textToMeasure = placeholderValue;
      }

      // Ensure we have a minimum text for measurement
      if (!textToMeasure || textToMeasure.length === 0) {
        textToMeasure = datePickerFormat;
      }

      const inputStyle: CSSStyleDeclaration = getComputedStyle(inputElement);
      const inputFont: string = `${inputStyle.fontSize} ${inputStyle.fontFamily}`;
      const inputValueWidth: number = getInputValueWidthByFont(textToMeasure, inputFont);

      // Add a small padding to avoid text break
      // For placeholders, we need a bit more space
      const widthPadding: number = inputValue.length > 0 ? 0 : 1;
      const finalWidth: number = Math.round(inputValueWidth) + widthPadding;

      setInputWidthByValue(finalWidth);
    }
  }, [datePickerFormat]);

  /* Function to format a given Dayjs value according to the provided format. */
  const formatDate = useCallback((dateValue?: Dayjs | null, format?: string): string => {
    const realDate: Dayjs = dayjs(dateValue?.valueOf());

    const withLocale: Dayjs = realDate.locale(locale?.locale === 'pt-br' ? 'pt-br' : 'en');
    const formattedDate: string = withLocale.format(format ?? 'MMM DD, YY');

    return formattedDate.replace(/(\b[a-z])/g, (match) => match.toUpperCase());
  }, [locale?.locale]);

  /* Callback function to render the panel for the DatePicker. */
  const renderPanel = useCallback(
    (panelNode: React.ReactNode): React.ReactNode => (
      <PanelContainer isLandscapeMobile={isMobileOrLandscape}>
        {panelNode}
      </PanelContainer>
    ),
    [isMobileOrLandscape],
  );

  /* Function to disable dates that are greater than today. */
  const disabledDate = useCallback(
    (current: AntDDayjs): boolean => current && current > dayjs().endOf('day'),
    [],
  );

  // Handler for calendar changes (single control point)
  const handleCalendarChange = useCallback(
    (dateValue: RangeValue<AntDDayjs> | null): void => {
      if (type === DatePickerType.rangePicker) {
        if (interimRange && interimRange[0] && interimRange[1] && dateValue && dateValue[0]) {
          setInterimRange([dateValue[0], null]);
        } else {
          setInterimRange(dateValue);

          if (dateValue && dateValue[0] && dateValue[1]) {
            const [start, end] = dateValue as [AntDDayjs, AntDDayjs];

            const startDayjs: Dayjs = dayjs(start?.valueOf()).startOf('day');
            const endDayjs: Dayjs = dayjs(end?.valueOf()).endOf('day');

            const valueToPass: RangeValue<AntDDayjs> = [startDayjs, endDayjs];
            const formatToPass: [string, string] = [
              formatDate(startDayjs, datePickerFormat),
              formatDate(endDayjs, datePickerFormat),
            ];

            onChange(valueToPass, formatToPass);
          }
        }
      }
    },
    [
      type,
      interimRange,
      datePickerFormat,
      onChange,
      formatDate,
    ],
  );

  // Handler for final changes (cleanup and simple DatePicker)
  const handleChangeRangePicker = (
    dateValue: RangeValue<AntDDayjs> | AntDDayjs | null,
    formatString: [string, string] | string,
  ): void => {
    if (type === DatePickerType.rangePicker) {
      if (!dateValue) {
        setInterimRange(null);
        onChange(null, ['', '']);
      }
    } else {
      onChange(dateValue, formatString);
    }
  };

  useEffect((): (() => void) | undefined => {
    let cleanup: (() => void) | undefined;

    if (type === DatePickerType.rangePicker) {
      const inputElement: Element | null = document.querySelector(
        '.ant-picker-range .ant-picker-input:first-child input',
      );

      let observer: MutationObserver | null = null;

      if (inputElement) {
        observer = new MutationObserver(calculateInputWidth);
        observer.observe(inputElement, { attributes: true, attributeFilter: ['value'] });

        calculateInputWidth();
      }

      cleanup = () => {
        if (observer) {
          observer.disconnect();
        }
      };
    }

    return cleanup;
  }, [type, locale, calculateInputWidth]);

  // Synchronize the interim state with the external value
  useEffect(() => {
    if (type === DatePickerType.rangePicker) {
      setInterimRange(value as RangeValue<AntDDayjs>);
    }
  }, [value, type]);

  return (
    <div className={className} data-testid={testId}>
      {type === DatePickerType.rangePicker
        ? (
          <RangePicker
            disabled={disabled}
            format={(dateValue) => formatDate(dateValue as Dayjs, datePickerFormat)}
            placement={placement}
            onCalendarChange={handleCalendarChange}
            onChange={(
              dateValue: RangeValue<AntDDayjs>,
              formatString: [string, string],
            ) => handleChangeRangePicker(dateValue, formatString)}
            value={interimRange}
            panelRender={renderPanel}
            inputReadOnly
            separator="-"
            width={inputWidthByValue}
            allowClear={{ clearIcon: <Close /> }}
            disabledDate={disabledDate}
          />
        ) : (
          <DatePickerComponent
            disabled={disabled}
            format={(dateValue) => formatDate(dateValue as Dayjs, datePickerFormat)}
            placement={placement}
            onChange={(
              dateValue: AntDDayjs | null,
              formatString: string,
            ) => handleChangeRangePicker(dateValue, formatString)}
            value={value as AntDDayjs | null}
            panelRender={renderPanel}
            inputReadOnly
            allowClear={{ clearIcon: <Close /> }}
          />
        )}
    </div>
  );
}

export { DatePicker };
